import { AxiosResponse, isAxiosError } from "axios";
import useSWR, { KeyedMutator } from "swr";
import { Category, CategoryTree, LandingPath } from "@/models/categoryTree";
import ApiService, { defaultSWROption } from "@/services/api/api.service";
import {
  CategoryNodeSchema,
  CategoryTreeSchema,
  LandingPathSchema,
} from "@/services/categoryTree/categoryTree.api.types";
import CategoryTreeEndpoints from "@/services/categoryTree/categoryTree.endpoint";

export class CategoryTreeService {
  static getCategoryTrees(workspaceId: string): {
    data: CategoryTree[] | undefined;
    error: unknown;
    isLoading: boolean;
    mutate: KeyedMutator<AxiosResponse<CategoryTreeSchema[], undefined>>;
  } {
    const url = workspaceId
      ? CategoryTreeEndpoints.GET_CATEGORY_TREES_BY_WORKSPACE(workspaceId)
      : CategoryTreeEndpoints.GET_CATEGORY_TREES;

    const fetcher = () =>
      ApiService.fetchData<undefined, CategoryTreeSchema[]>({
        url,
        method: "GET",
      });

    // eslint-disable-next-line react-hooks/rules-of-hooks
    const { data, error, isLoading, mutate } = useSWR(
      url,
      fetcher,
      defaultSWROption,
    );

    return {
      data: data?.data.map(buildCategoryTree),
      error,
      isLoading,
      mutate,
    };
  }

  static getCategoryTree(id: string | undefined): {
    data: CategoryTree | undefined;
    error: unknown;
    isLoading: boolean;
  } {
    const fetcher = () =>
      ApiService.fetchData<undefined, CategoryTreeSchema>({
        url: id ? CategoryTreeEndpoints.GET_CATEGORY_TREE(id) : "",
        method: "GET",
      });

    // eslint-disable-next-line react-hooks/rules-of-hooks
    const { data, error, isLoading } = useSWR(
      id ? CategoryTreeEndpoints.GET_CATEGORY_TREE(id) : null,
      fetcher,
      defaultSWROption,
    );

    return {
      data: data ? buildCategoryTree(data.data) : undefined,
      error,
      isLoading,
    };
  }

  static getLandingPathsByWorkspaceId(workspaceId: string): {
    data: LandingPath[] | undefined;
    error: unknown;
    isLoading: boolean;
    mutate: KeyedMutator<AxiosResponse<LandingPathSchema[], undefined>>;
  } {
    const url = CategoryTreeEndpoints.GET_LANDING_PATHS_BY_WORKSPACE(workspaceId);

    const fetcher = () =>
      ApiService.fetchData<undefined, LandingPathSchema[]>({
        url,
        method: "GET",
      });

    // eslint-disable-next-line react-hooks/rules-of-hooks
    const { data, error, isLoading, mutate } = useSWR(
      url,
      fetcher,
      defaultSWROption,
    );

    return {
      data: data?.data.map(
        (path) => new LandingPath(path.category_path, path.contained_versions),
      ),
      error,
      isLoading,
      mutate,
    };
  }

  static async upload(
    defaultMaxChildren: number[],
    maxClassifications: number[],
    file: File,
    workspaceId: string,
  ) {
    return new Promise<void>((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = async () => {
        try {
          const data = {
            workspaceId,
            defaultMaxChildren,
            maxClassifications,
            file: String(reader.result).split(",")[1],
          };
          await ApiService.fetchData<typeof data, CategoryTreeSchema>({
            url: CategoryTreeEndpoints.UPLOAD,
            method: "POST",
            data,
          });
          resolve();
        } catch (error) {
          if (isAxiosError(error) && error.response?.data?.detail) {
            reject(error.response.data.detail);
          } else {
            reject(["不明なエラーが発生しました。"]);
          }
        }
      };
      reader.readAsDataURL(file);
    });
  }

  static async download(categoryTreeId: string): Promise<Blob> {
    const response = await ApiService.fetchData<undefined, Blob>({
      url: CategoryTreeEndpoints.DOWNLOAD(categoryTreeId),
      method: "GET",
      responseType: "blob",
    });

    return response.data;
  }
}

const buildCategoryTree = (categoryTree: CategoryTreeSchema) =>
  new CategoryTree(
    categoryTree.id,
    categoryTree.version,
    categoryTree.categories?.map(buildCategory) ?? [],
  );

const buildCategory = (categoryNode: CategoryNodeSchema) =>
  new Category(
    categoryNode.id,
    categoryNode.name,
    categoryNode.child_count,
    categoryNode.level,
    categoryNode.parent_id,
  );
